import type { Ref } from "vue";
import type { SearchProvider, SearchResult } from "./searchProvider";
import type { Section, SuggestionItem } from "./docSearch/types";
import { buildSearchIndex, filterAndRank } from "./docSearch/score";
import { searchNormalize } from "./docSearch/normalize";
import { slugToCollection } from "~/utils/siteLocale";
import { queryPages, querySearchSections } from "~/utils/contentQueries";
import {
  isDocsUrl,
  siteFromUrl,
  contentPathToUrl,
} from "~/utils/routeMapping";

/**
 * Unified search provider — searches BOTH zones (commercial + docs) at once,
 * everywhere on the site. Replaces the per-layout commercial/docs providers.
 *
 * Both indexes are merged into one ranked list. Each entry is tagged with its
 * `zone`; the scorer adds a bonus to entries in the zone the user is currently
 * browsing (read reactively from the route), so a commercial page ranks higher
 * while you're on a commercial page — but a direct title hit in docs still
 * surfaces because the zone bonus is smaller than the title-match bonuses.
 *
 * The api-reference layout keeps its own in-page operation search and overrides
 * this provider while mounted (see app.vue for the re-assert on leaving).
 */
export function useUnifiedSearchProvider(locale: Ref<string>): SearchProvider {
  const route = useRoute();

  const sections = ref<Section[]>([]);
  const commercialSuggestions = ref<SuggestionItem[]>([]);
  const docsSuggestions = ref<SuggestionItem[]>([]);
  const isLoading = ref(false);
  const loaded = ref(false);
  // Quick-navigation suggestions are page-level (no markdown parse), so they
  // resolve in a fast first phase; the heavy heading-section index loads in the
  // background behind them. `suggestionsReady` gates re-running that fast phase.
  const suggestionsReady = ref(false);
  const searchTerm = ref("");
  // Debounced view of the query that drives ranking + the rendered list. The
  // raw `searchTerm` still updates the input immediately; coalescing the
  // expensive part (scan over ~15k sections + re-render of the result rows)
  // to ~80ms after the last keystroke keeps fast typing smooth. Hand-rolled
  // rather than @vueuse/core's refDebounced: importing @vueuse here pulled a
  // new dep into the graph, triggering Vite dep re-optimization that broke
  // dynamically-imported modules (_contentClient, SearchOverlay) mid-session.
  const debouncedTerm = ref("");
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  watch(searchTerm, (v) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debouncedTerm.value = v;
    }, 80);
  });

  // Per-locale snapshot of a completed load. Switching locale and back is free —
  // no re-query, no re-deserialize of the content DB.
  const cache = new Map<
    string,
    { sections: Section[]; commercial: SuggestionItem[]; docs: SuggestionItem[] }
  >();

  // Zone + active docs sub-site derived from the current route. Reactive, so
  // weighting follows SPA navigation without re-providing the provider.
  const currentZone = computed<"commercial" | "docs">(() =>
    isDocsUrl(route.path) ? "docs" : "commercial",
  );
  const activeSite = computed(() =>
    isDocsUrl(route.path) ? siteFromUrl(route.path) || undefined : undefined,
  );

  // Monotonic load generation. Bumped on every locale change and at the start
  // of each load(); an in-flight load whose captured gen no longer matches is
  // stale (locale switched or a newer load started) and discards its result
  // instead of writing it under the wrong locale.
  let loadGen = 0;

  watch(locale, () => {
    loadGen++; // invalidate any in-flight load
    sections.value = [];
    commercialSuggestions.value = [];
    docsSuggestions.value = [];
    isLoading.value = false;
    suggestionsReady.value = false;
    const wasLoaded = loaded.value;
    loaded.value = false;
    // If search is already in active use (overlay open / a prior load ran),
    // re-load for the new locale — the watcher is the only thing that knows the
    // locale changed; SearchOverlay only calls load() once on open.
    if (wasLoaded) load();
  });

  type LoadResult = { sections: Section[]; suggestions: SuggestionItem[] };

  // ── Commercial index ──────────────────────────────────────────────────────
  // Page-level entries from the `commercial` collection (deep leaves included).
  // Pure with respect to the locale argument — returns its suggestions instead
  // of mutating shared refs mid-flight, so a stale load can't pollute state.
  async function loadCommercial(loc: string): Promise<LoadResult> {
    const allPages =
      (await (await queryPages("commercial"))
        .where("stem", "LIKE", `${loc}/%`)
        .select("path", "stem", "title", "description", "icon", "listed", "redirect", "navigation")
        .all()) ?? [];

    // Drop `listed: false` archive articles (unlisted → not searchable) and
    // redirect stubs (old slugs that only forward to a real page, which is
    // indexed itself). Kept in-memory so pages without the keys stay indexed.
    const localePages = allPages.filter(
      (item) =>
        !!item.path &&
        (item as { listed?: boolean }).listed !== false &&
        !(item as { redirect?: string }).redirect,
    );

    // Suggestions: top-level commercial section indexes `{locale}/{N.slug}/index`
    // that are in the site navigation (legal pages and other `navigation: false`
    // indexes stay searchable but are not places to jump to).
    const suggestions: SuggestionItem[] = localePages
      .flatMap((item) => {
        if ((item as { navigation?: unknown }).navigation === false) return [];
        const parts = (item.stem as string).split("/");
        if (parts.length !== 3 || parts[2] !== "index") return [];
        const raw = parts[1] ?? "";
        const slug = raw.replace(/^\d+\./, "");
        const order = parseInt(raw) || 0;
        return slug ? [{ slug, order, item }] : [];
      })
      .sort((a, b) => a.order - b.order)
      .map(({ slug, item }) => ({
        id: "/" + loc + "/" + slug,
        label: searchNormalize(item.title ?? slug),
        description: searchNormalize(item.description),
        icon: item.icon,
      }));

    // Breadcrumb = ancestor section-index titles.
    const byStem = new Map<string, any>();
    for (const p of localePages) byStem.set(p.stem as string, p);
    const breadcrumb = (stem: string): string[] => {
      const parts = stem.split("/");
      const crumbs: string[] = [];
      for (let i = 2; i < parts.length; i++) {
        const candidate = parts.slice(0, i).join("/") + "/index";
        const ancestor = byStem.get(candidate);
        if (ancestor?.title && ancestor.stem !== stem)
          crumbs.push(searchNormalize(ancestor.title));
      }
      return crumbs;
    };

    const out = localePages.map(
      (item) =>
        ({
          id: item.path as string,
          title: searchNormalize(item.title as string),
          titles: breadcrumb(item.stem as string),
          content: searchNormalize(item.description as string),
          level: 1,
          zone: "commercial",
        }) as Section,
    );
    return { sections: out, suggestions };
  }

  // ── Docs index ────────────────────────────────────────────────────────────
  type DocsSite = { slug: string; order: number; item: any };

  // Cheap: the docs sub-site index pages (page-level, no markdown parse). Drives
  // both the quick-nav suggestions and the per-site section queries.
  async function docsLocaleSites(loc: string): Promise<DocsSite[]> {
    const allPages =
      (await (await queryPages("sections"))
        .where("stem", "LIKE", `${loc}/%`)
        .select("stem", "title", "description", "icon")
        .all()) ?? [];

    return allPages
      .flatMap((item) => {
        const parts = ((item.stem as string) ?? "").split("/");
        if (parts.length !== 4 || parts[3] !== "index") return [];
        if (parts[1] !== "docs" && !/^\d+\.docs$/.test(parts[1] ?? "")) return [];
        const raw = parts[2] ?? "";
        const slug = raw.replace(/^\d+\./, "");
        const order = parseInt(raw) || 0;
        return slug ? [{ slug, order, item }] : [];
      })
      .sort((a, b) => a.order - b.order);
  }

  function docsSuggestionsFrom(sites: DocsSite[], loc: string): SuggestionItem[] {
    return sites.map(({ slug, item }) => ({
      id: "/" + loc + "/docs/" + slug,
      label: searchNormalize(item.title ?? slug),
      description: searchNormalize(item.description),
      icon: item.icon,
    }));
  }

  // Heavy: heading-level chunks from each docs sub-site collection. This is the
  // markdown-AST parse that dominates load time, so it runs in the background
  // phase (see load()) behind the already-visible quick-nav suggestions.
  async function loadDocsSections(
    sites: DocsSite[],
    loc: string,
  ): Promise<Section[]> {
    // Per-site, fully isolated: a failure resolving one site's collection (or
    // its section query) yields [] for that site only — it must not reject the
    // whole Promise.all and wipe every other site's docs results. The await on
    // slugToCollection is inside the try so its rejection is caught too.
    const chunks = await Promise.all(
      sites.map(async ({ slug }) => {
        try {
          const col = await slugToCollection(slug);
          if (!col) return [] as Section[]; // unknown slug → no backing collection
          // Restrict to the target locale at the SQL level — the collection
          // spans all locales, so without this we'd parse ~7× the markdown.
          return await querySearchSections(col, {
            extraFields: ["search_boost"],
            stemLike: `${loc}/%`,
          });
        } catch {
          return [] as Section[];
        }
      }),
    );

    return (chunks.flat() as Section[])
      .filter((s) => (s.title || s.content) && !s.id.includes("/blocks/"))
      .map((s) => ({
        ...s,
        title: searchNormalize(s.title),
        titles: (s.titles ?? []).map((t) => searchNormalize(t)),
        content: searchNormalize(s.content),
        zone: "docs" as const,
      }));
  }

  const EMPTY: LoadResult = { sections: [], suggestions: [] };

  // Two-phase load:
  //  Phase 1 (awaited, fast): quick-nav suggestions + the page-level commercial
  //    index. No markdown parsing, so the palette is usable in well under a
  //    second — suggestions render immediately and commercial pages are already
  //    searchable.
  //  Phase 2 (background, heavy): the docs heading-section index (the markdown
  //    AST parse). Merged into `sections` when it resolves; until then full-text
  //    docs results just aren't there yet (`isLoading` drives the “Loading…”
  //    state in the results area). Invisible to a user reading the quick-nav.
  async function load() {
    if (isLoading.value || loaded.value) return;

    // Capture the locale this load is for — used both as the cache key and to
    // tag the result, so an in-flight load always writes under the locale it
    // actually queried, never under whatever locale.value reads at resolve time.
    const targetLocale = locale.value;

    const cached = cache.get(targetLocale);
    if (cached) {
      sections.value = cached.sections;
      commercialSuggestions.value = cached.commercial;
      docsSuggestions.value = cached.docs;
      suggestionsReady.value = true;
      loaded.value = true;
      return;
    }

    const gen = ++loadGen;
    isLoading.value = true;

    // ── Phase 1: suggestions + commercial sections (fast) ──
    const [commercial, sites] = await Promise.all([
      loadCommercial(targetLocale).catch((e) => {
        console.error("[Search] commercial load failed:", e);
        return EMPTY;
      }),
      docsLocaleSites(targetLocale).catch((e) => {
        console.error("[Search] docs sites load failed:", e);
        return [] as DocsSite[];
      }),
    ]);

    // Superseded by a locale change or a newer load — drop this result.
    if (gen !== loadGen) return;

    commercialSuggestions.value = commercial.suggestions;
    docsSuggestions.value = docsSuggestionsFrom(sites, targetLocale);
    // Commercial pages are searchable straight away; docs sections join later.
    sections.value = commercial.sections;
    suggestionsReady.value = true;

    // ── Phase 2: docs heading sections (heavy, background) ──
    loadDocsSections(sites, targetLocale)
      .then((docsSections) => {
        if (gen !== loadGen) return;
        sections.value = [...commercial.sections, ...docsSections];
        cache.set(targetLocale, {
          sections: sections.value,
          commercial: commercial.suggestions,
          docs: docsSuggestions.value,
        });
        loaded.value = true;
        if (import.meta.dev) {
          console.log(
            `[Search] indexed ${commercial.sections.length} commercial + ${docsSections.length} docs entries for ${targetLocale}`,
          );
        }
      })
      .catch((e) => console.error("[Search] docs sections load failed:", e))
      .finally(() => {
        // Only the current load clears the flag — a stale load resolving late
        // must not flip isLoading for the load that superseded it.
        if (gen === loadGen) isLoading.value = false;
      });
  }

  // Quick-navigation suggestions follow the current zone.
  const suggestions = computed<SuggestionItem[]>(() =>
    currentZone.value === "docs"
      ? docsSuggestions.value
      : commercialSuggestions.value,
  );

  // Prebuilt, locale-scoped search index. Recomputes only when the loaded
  // sections change (load / locale switch) — not on every keystroke. Each
  // keystroke then reuses the tokenized fields instead of re-deriving them.
  const localePrefix = computed(() => "/" + locale.value + "/");
  const indexed = computed(() =>
    buildSearchIndex(
      sections.value.filter((s) => s.id.startsWith(localePrefix.value)),
    ),
  );

  const filteredResults = computed<SearchResult[]>(() => {
    const q = searchNormalize(debouncedTerm.value.trim());
    if (!q) return [];
    return filterAndRank(
      indexed.value,
      q,
      localePrefix.value,
      activeSite.value,
      currentZone.value,
    ).map((s) => ({
      id: s.id,
      title: s.title,
      titles: s.titles,
      content: s.content,
      level: s.level,
      zone: s.zone,
    }));
  });

  function resolveUrl(id: string): string {
    return contentPathToUrl(id);
  }

  return {
    isLoading,
    searchTerm,
    suggestions,
    filteredResults,
    placeholder: "Search…",
    load,
    resolveUrl,
  };
}
